import { CategoryType } from "../rules/rulesAPI";

export type TransactionType = "EXPENSE" | "REFUND";
export type TransactionStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "SUSPICIOUS"
  | "PENDING_ADJUSTEMENT"
  | "PENDING_CAPTURE"
  | "PENDING_CONFIRMATION"
  | "CANCELED";

export type PublicTransactionStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "SUSPICIOUS"
  | "PENDING"
  | "CANCELED";

export type SupportingDocumentStatus = "ACCEPTED" | "MISSING" | "";

export type EmspAuthorizationStatus =
  | "ALLOWED"
  | "NOT_ALLOWED"
  | "NO_CREDIT"
  | "EXPIRED"
  | "BLOCKED";

export type EmspLocationType =
  | "ON_STREET"
  | "PARKING_GARAGE"
  | "UNDERGROUND_GARAGE"
  | "PARKING_LOT"
  | "OTHER"
  | "UNKNOWN";

export type PublicTransactionType = {
  uuid: string;
  amount: number;
  executionDate: string;
  registrationNumber: string;
  merchantName: string;
  merchantCity: string;
  merchantPostalCode: string | null;
  mileage: number | null;
  quantity: number;
  consumption: string;
  cardLast4Digits: string;
  driverName: string;
  driverPhone: string;
  supportingDocumentUuid?: string;
  status: TransactionStatus;
  vehicleReference: string;
  driverLastName: string;
  driver: string;
  ruleName: string;
  rule: string;
  vehicleUuid: string;
  card: string;
  category: CategoryType;
  fuel: string;
  carbonEmission: number;
  hasToRetrieveSupportingDocuments: boolean;
  merchantGroup: MerchantGroups | null;
  vehicleAnalyticCode: string | null;
  type: TransactionType;
};

export type PublicAuthorizationType = {
  uuid: string;
  executionDate: string;
  amount: number;
  merchantName: string;
  merchantCode: string;
  merchantCity: string;
  reasonsRefused: ReasonsRefusedEnum[];
  registrationNumber: string | null;
  cardLast4Digits: string;
  driverFirstName: string | null;
  driverLastName: string | null;
  driver: string | null;
  ruleName: string;
  ruleUuid: string;
  vehicleUuid: string;
  cardUuid: string;
  vehicleReference: string;
  category: CategoryType | null;
  merchantGroup: MerchantGroups | null;
  categoryAlreadyCorrected: boolean;
  vehicleAnalyticCode: string | null;
};

export type PublicTransactionDisplayed = {
  date: string;
  time: string;
  amount: number | null;
  merchantName: string;
  merchantGroup: MerchantGroups | null;
  category: CategoryType | null;
  driverName: string | null;
  status: PublicTransactionStatus;
  supportingDocumentStatus: SupportingDocumentStatus;
  mileage: number | null;
  fuel: string | null;
  quantity: number | null;
  vehicleReference: string | null;
};

export type BadgeTransactionDisplayedApi = {
  uuid: string;
  executionDate: string;
  badgeNumber: string;
  category: "TOLL" | "PARKING";
  amount: number | null;
  registrationNumber: string;
  reference: string | null;
  type: TransactionType;
  entranceStationName: string;
  exitStationName: string;
};

export type PublicBadgeTransaction = {
  uuid: string;
  date: string;
  badgeNumber: string;
  category: "TOLL" | "PARKING";
  amount: number | null;
  vehicleInfo: string;
  stationsInfo: string;
  type: TransactionType;
  entranceStationName: string;
  exitStationName: string;
};

export type PublicBadgeTransactionDisplayed = {
  date: string;
  badgeNumber: string;
  category: "TOLL" | "PARKING";
  amount: number | null;
  vehicleInfo: string;
  stationsInfo: string;
};

export type PublicEmspCdrsDisplayed = {
  startAt: string;
  stopAt: string;
  totalCost: number;
  totalEnergy: number;
  totalTime: number;
  totalParkingTime: number;
  authorizationStatus: EmspAuthorizationStatus;
  locationCity: string;
  registrationNumber: string;
  identifier: string;
  vehicleReference: string;
};

export type PublicEmspCdrs = PublicEmspCdrsDisplayed & {
  uuid: string;
  cardUuid: string;
  vehicleUuid: string;
  currency: string;
  locationName: string;
  locationType: string;
  locationCountry: string;
  locationPostalCode: string;
  locationAddress: string;
  last4Digits: string;
};

export type CreateMerchantCode = {
  category: CategoryType;
  authorizationUuid: string;
  companyUuid: string;
};

export type PublicTransactionsPropsType = {
  uuid: string;
  amount: number | null;
  registrationNumber: string | null;
  merchantName: string;
  merchantCity: string;
  cardLast4Digits: string;
  driverName: string | null;
  driverPhone?: string;
  status: PublicTransactionStatus;
  date: string;
  time: string;
  reasonsRefused?: ReasonsRefusedEnum[] | null;
  mileage: number | null;
  consumption?: string;
  supportingDocumentUuid?: string;
  vehicleReference: string | null;
  supportingDocumentStatus: SupportingDocumentStatus;
  driverLastName: string | null;
  driver: string | null;
  ruleName: string;
  ruleUuid: string;
  vehicleUuid: string;
  cardUuid: string;
  category: CategoryType | null;
  fuel: string | null;
  quantity: number | null;
  carbonEmission?: number;
  areSupportingDocumentsMandatory?: boolean;
  merchantGroup: MerchantGroups | null;
  categoryAlreadyCorrected: boolean | null;
  type: TransactionType;
};

export type GetSupportingDocumentType = {
  presignedUrl: string;
  fileName: string;
};

import { type SendSmsReminderPayload } from "@qivia/schemas-reminders";

export type SendSmsPayload = SendSmsReminderPayload;

export type TotalAmountPerCategoryType = {
  category: CategoryType;
  amount: number;
};

export type TotalAmountPerMerchantGroupType = {
  merchantGroup: MerchantGroups | null;
  amount: number;
};

export type DateValueType = {
  date: string;
  value: number;
};

export type AveragePricePerLiterPerFuelType = {
  fuel: string;
  data: DateValueType[];
};

export type GetTransactionsAmountsPerPeriodType = {
  year: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
  month: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
  quarter: {
    categoriesAmounts: TotalAmountPerCategoryType[];
    averagePricePerLiter: DateValueType[];
    totalCarbonEmission: DateValueType[];
    merchantGroupsAmounts: TotalAmountPerMerchantGroupType[];
    averagePricePerLiterPerFuel: AveragePricePerLiterPerFuelType[];
  };
};

export type ReasonsRefusedEnum =
  | "NO_DRIVER"
  | "NO_FUNDS"
  | "EXCEEDED_LIMITS"
  | "WRONG_TIME"
  | "WRONG_DAY"
  | "WRONG_PLACE"
  | "WRONG_CURRENCY"
  | "UNAUTHORIZE_MERCHANT"
  | "UNAUTHORIZE_MERCHANT_CODE"
  | "UNKNOWN_MERCHANT_CODE"
  | "UNAUTHORIZE_MERCHANT_NAME"
  | "UNKNOWN_MERCHANT_NAME"
  | "INACTIVE_CARD"
  | "IN_OPPOSITION_CARD"
  | "INACTIVE_PIN_ONLINE_CARD"
  | "BLOCKED_PIN_OFFLINE_CARD"
  | "EARLY_RETRY"
  | "ERROR"
  | "STRIPE_DASHBOARD"
  | "SUSPECTED_FRAUD"
  | "NO_WEBHOOK_RESPONSE"
  | "PIN_BLOCKED"
  | "ROTATION_REQUEST_TRANSACTIONS_EXCEEDED"
  | "ALREADY_REJECTED_ROTATION_REQUEST"
  | "INACTIVE_COMPANY"
  | "OTHER"
  | "ONLINE_PAYMENT_UNAUTHORIZED";

export type MerchantGroups =
  | "SHELL"
  | "BP"
  | "ENI"
  | "TOTAL_ENERGIES"
  | "AVIA"
  | "ESSO"
  | "CASINO"
  | "CARREFOUR"
  | "INTERMARCHE"
  | "AUCHAN"
  | "SYSTEME_U"
  | "LECLERC";

export const MerchantGroupsList = [
  "SHELL",
  "BP",
  "ENI",
  "TOTAL_ENERGIES",
  "AVIA",
  "ESSO",
  "CASINO",
  "CARREFOUR",
  "INTERMARCHE",
  "AUCHAN",
  "SYSTEME_U",
  "LECLERC",
];

export type PayloadForGetTransactionsByCompanyByPeriod = {
  companyUuid: string;
  from?: string;
  to?: string;
};

export type PayloadForGetTransactionsOfADepartmentByPeriod = {
  departmentUuid: string;
  from?: string;
  to?: string;
};

export type TransactionByPeriodType = {
  uuid: string;
  date: string;
  driver: string | null;
};

export type PayloadForGetAuthorizationsRejectedOfADepartmentByPeriod = {
  departmentUuid: string;
  from?: string;
  to?: string;
};

export type PayloadForGetAuthorizationsRejectedByCompanyByPeriod = {
  companyUuid: string;
  from?: string;
  to?: string;
};

export type UploadSupportingDocumentResponse = {
  supportingDocumentId: string;
  fileName: string;
};

export type PayloadForUpdateTransactionDetails = {
  transactionUuid: string;
  mileage: number;
  updatedBy: string;
};
